import { ShieldCheck, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { ANTIVIRUS_PRODUCTS, ANTIVIRUS_ROWS, ANTIVIRUS_FAQS } from "@/data/content";

const Antivirus = () => (
  <main data-testid="antivirus-page">
    <Nav />

    <header className="pt-40 pb-16 px-6">
      <div className="max-w-5xl mx-auto">
        <p className="font-mono-p text-xs tracking-[0.35em] uppercase text-[#0055FF] mb-6">
          Special Report — Security Suites
        </p>
        <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="antivirus-title">
          <MaskedLine delay={0.2}>Antivirus, compared.</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-stroke">Seven suites. Two picks.</span>
          </MaskedLine>
        </h1>
        <p className="font-mono-p text-sm md:text-base text-[#A1A1AA] max-w-2xl mt-10 leading-relaxed">
          We compared the seven most-installed antivirus products on lab scores,
          system impact, price and honesty of conduct. Most people need exactly
          two answers — and one of them is already on their PC.
        </p>
      </div>
    </header>

    <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="antivirus-scorecard">
      <Reveal>
        <div className="border border-white/10">
          {ANTIVIRUS_ROWS.map((row, i) => (
            <div
              key={row.label}
              data-testid={`antivirus-row-${i}`}
              className="grid md:grid-cols-[1fr_2fr] border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] transition-colors duration-300"
            >
              <div className="p-5 font-oswald text-sm tracking-[0.15em] uppercase text-[#F5F5F5] bg-[#0A0A0A]/50 flex items-center gap-3">
                <Star className="w-3.5 h-3.5 text-[#0055FF] shrink-0" />
                {row.label}
              </div>
              <div className="p-5 font-mono-p text-sm text-[#A1A1AA] border-t md:border-t-0 md:border-l border-white/10">
                {row.detail}
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>

    <section className="max-w-6xl mx-auto px-6 pb-28" data-testid="antivirus-products">
      <Reveal className="mb-12">
        <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl tracking-tight">The full field, ranked by honesty.</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6">
        {ANTIVIRUS_PRODUCTS.map((p, i) => (
          <Reveal key={p.name} delay={(i % 2) * 0.12}>
            <article
              data-testid={`antivirus-card-${i}`}
              className={`h-full border p-8 md:p-10 bg-[#121212]/40 transition-shadow duration-500 ${
                p.pick ? "border-[#0055FF]/50 glow-win" : "border-white/10 glow-neutral"
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <span className="font-oswald text-xs tracking-[0.3em] uppercase text-[#A1A1AA] block mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif-d text-2xl md:text-3xl leading-tight">{p.name}</h3>
                </div>
                {p.pick && (
                  <span className="flex items-center gap-2 font-oswald text-[10px] tracking-[0.25em] uppercase text-[#0055FF] border border-[#0055FF]/40 px-3 py-1.5 shrink-0">
                    <ShieldCheck className="w-3.5 h-3.5" /> Our pick
                  </span>
                )}
              </div>
              <dl className="space-y-3 mb-8">
                {[
                  ["Price", p.price],
                  ["Protection", p.protection],
                  ["System impact", p.impact],
                  ["Platforms", p.platforms],
                  ["Best for", p.bestFor],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[110px_1fr] gap-4 border-b border-white/5 pb-3">
                    <dt className="font-mono-p text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] pt-0.5">{k}</dt>
                    <dd className="font-mono-p text-xs text-[#c9c9cf] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className={`font-mono-p text-sm leading-relaxed border-l-2 pl-4 ${p.pick ? "text-[#F5F5F5] border-[#0055FF]" : "text-[#A1A1AA] border-white/20"}`}>
                {p.verdict}
              </p>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="max-w-5xl mx-auto px-6 pb-28" data-testid="antivirus-faq">
      <Reveal className="mb-12">
        <p className="font-mono-p text-xs tracking-[0.35em] uppercase text-[#FF3300] mb-4">Security Questions</p>
        <h2 className="font-serif-d text-3xl sm:text-4xl lg:text-5xl tracking-tight">What readers actually ask.</h2>
      </Reveal>
      <Reveal>
        <Accordion type="single" collapsible data-testid="antivirus-faq-accordion">
          {ANTIVIRUS_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`av-faq-${i}`} className="border-b border-white/10">
              <AccordionTrigger
                data-testid={`antivirus-faq-trigger-${i}`}
                className="font-serif-d text-lg md:text-xl text-left py-7 hover:text-[#0055FF] hover:no-underline transition-colors duration-300"
              >
                <span className="flex items-baseline gap-5">
                  <span className="font-oswald text-sm text-stroke shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {f.q}
                </span>
              </AccordionTrigger>
              <AccordionContent
                data-testid={`antivirus-faq-content-${i}`}
                className="font-mono-p text-sm leading-relaxed text-[#A1A1AA] pb-7 md:pl-12 max-w-3xl"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <Footer />
  </main>
);

export default Antivirus;
